import SearchConditionRow from "@/components/SearchConditionRow";
import SearchConditionTag, {
  SearchConditionTagType,
} from "@/components/SearchConditionTag";
import { FC, PropsWithChildren } from "react";
import useCategories from "@/hooks/useCategories";
import useProductSearchCondition from "@/features/product_variation/hooks/useProductSearchCondition";
import useDisclosure from "@/hooks/useDisclosure";
import ModalSearchProduct from "@/features/product_variation/components/ModalSearchProduct";
import { ProductSearchQueryParameter } from "@/types/ProductSearchQueryParameter";
import { SearchQueryParameterController } from "@/hooks/useSearchQueryParameter";
import SearchGroup from "@/components/SearchGroup";
import SearchProductDropdowns from "@/components/SearchProductDropdowns";
import { ProductKind } from "@/web-client/api";
import useProductSearchGroup from "@/features/product_variation/hooks/useProductSearchGroup";
import Tabs from "@/components/Tabs";
import { ProductSearchHeader } from "@/features/sort";

interface Props {
  sidebar: JSX.Element;
  showCondition?: boolean;
  headerView?: JSX.Element;
  totalCount: number;
  query: ProductSearchQueryParameter;
  queryController: SearchQueryParameterController<ProductSearchQueryParameter>;
  kind: ProductKind;
  isSearchingPosts?: boolean;
}

const ProductSearchGroup: FC<PropsWithChildren<Props>> = ({
  children,
  sidebar,
  headerView,
  totalCount,
  query,
  queryController,
  kind,
  isSearchingPosts,
}): JSX.Element => {
  const { categories } = useCategories();
  const { hasCondition, conditions } = useProductSearchCondition(
    query,
    categories,
  );

  const { openHandler, closeHandler, isOpen } = useDisclosure();

  const { tabs } = useProductSearchGroup(kind, query, isSearchingPosts);

  return (
    <SearchGroup
      headerView={headerView}
      hasCondition={hasCondition}
      dropdowns={
        <SearchProductDropdowns
          onOpenModal={openHandler}
          categories={categories}
          query={query}
          queryController={queryController}
          kind={kind}
        />
      }
      modal={
        <ModalSearchProduct
          kind={kind}
          query={query}
          closeHandler={closeHandler}
        />
      }
      modalOpen={isOpen}
      sidebar={sidebar}
      conditions={
        <SearchConditionRow
          basePath={queryController.basePath}
          visibleClearBtn={hasCondition}
        >
          {conditions.map((condition) => {
            return (
              <SearchConditionTag
                key={condition.label}
                name={condition.label}
                type={SearchConditionTagType.Delete}
                onClick={() => {
                  queryController.deleteQueryAt(condition.key, condition.value);

                  // カテゴリが削除された場合は、詳細検索項目も削除
                  if (condition.key === "category_id") {
                    queryController.deleteQueryAt(
                      "search_condition_value_ids",
                      query.search_condition_value_ids,
                    );
                  }

                  queryController.submitQuery();
                }}
              />
            );
          })}
        </SearchConditionRow>
      }
    >
      <div className="space-y-12 laptop:space-y-20">
        <Tabs tabs={tabs} amount={totalCount} />
        <ProductSearchHeader
          queryController={queryController}
          query={query}
          sortable={!isSearchingPosts}
        />

        {children}
      </div>
    </SearchGroup>
  );
};

export default ProductSearchGroup;
